import { LoginCredentials, AuthResponse, RegisterForm } from '../types/auth';
import { fetchApiLogin, fetchApi } from '../utils/api';
import { showAlert } from '../utils/toast';
import { LocalKeys, hasLocalData, getLocalData, setLocalData } from '../hooks/useLocalData';

class AuthService {
  // Use the LocalKeys enum to ensure consistent keys across the application
  private readonly HOME_LINKS_KEY = LocalKeys.HOME_LINKS;
  private readonly USER_TOKEN_KEY = '@USER_TOKEN';
  private readonly USERNAME_KEY = '@USERNAME';
  private readonly TELEGRAM_ID_KEY = '@TELEGRAM_ID';
  private readonly PROFILE_DATA_KEY = LocalKeys.PROFILE_DATA;
  private readonly DEVICE_ID_KEY = 'device_id';
  private readonly FORCE_LOGOUT_FLAG = '@FORCE_LOGOUT';
  private readonly LAST_USERNAME_KEY = '@LAST_USERNAME';

  // Custom event to notify when logout occurs
  private readonly LOGOUT_EVENT = 'vf_logout_event';

  /**
   * Generates and manages device ID
   * Checks if device_id already exists in localStorage
   * If not, generates a new unique ID and stores it
   * @returns {string} The device ID
   */
  generateDeviceId(): string {
    // Check if device_id already exists in localStorage
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
    
    // If device_id doesn't exist, generate a new one
    if (!deviceId) {
      console.log('Generating new device ID');
      // Generate a UUID v4
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      
      // Store the new device ID in localStorage
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
      console.log('New device ID generated and stored:', deviceId);
    } else {
      console.log('Using existing device ID');
    }
    
    return deviceId;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Xóa toàn bộ sessionStorage trước khi đăng nhập để đảm bảo không sử dụng dữ liệu cũ
      console.log('[Auth] Login: Clearing all session storage data');
      sessionStorage.clear();
      
      const data = await fetchApiLogin(
        'post',
        'token',
        { username: credentials.username, password: credentials.password, grant_type: 'password' }
      )
      if (data.error) {
        throw Error(data.error_description)
      }
      if (data) {
        // Store auth response and username directly in localStorage
        localStorage.setItem(this.USER_TOKEN_KEY, JSON.stringify(data));
        localStorage.setItem(this.USERNAME_KEY, credentials.username);
        
        // Generate or retrieve the device ID
        this.generateDeviceId();
        
        // Load initial data including profile after successful login
        try {
          await this.loadInitialData();
        } catch (dataError) {
          console.error('Failed to load initial data after login:', dataError);
          // Continue with login even if data loading fails
        }
      }
      return data;
    } catch (error: any) {
      showAlert('error', error.message)
      throw error;
    }
  }

  async register(formData: RegisterForm) {
    try {
      const data = await fetchApiLogin(
        'post',
        'api/Account/SignUp',
        {
          FullName: formData.fullName, 
          Phone: formData.phone,
          Email: formData.email,
          BigShareHolder: formData.stocksInput,
          Cmnd: formData.passportNumber
        }
      )
      if (data.Status !== 'Success') throw new Error(data.Message);
      return data;
    } catch (error: any) {
      showAlert('error', error.message);
      throw error;
    }
  }

  clearAuth(): void {
    // Xóa toàn bộ sessionStorage trước
    console.log('[Auth] Clearing all session storage data');
    sessionStorage.clear();
    
    // Xóa dữ liệu từ localStorage
    localStorage.removeItem(this.USER_TOKEN_KEY);
    localStorage.removeItem(this.USERNAME_KEY);
    localStorage.removeItem(this.HOME_LINKS_KEY);
    localStorage.removeItem(this.PROFILE_DATA_KEY);
    localStorage.removeItem(this.TELEGRAM_ID_KEY);
    // All other data is now fetched directly from API
    
    // Reset initialization flag khi clear auth
    if (window && 'vfIsAuthInitialized' in window) {
      window.vfIsAuthInitialized = false;
    }
  }

  logout(): void {
    // Save the current username before logout
    const currentUsername = this.getUsername();
    if (currentUsername) {
      localStorage.setItem(this.LAST_USERNAME_KEY, currentUsername);
      console.log('Saved last username:', currentUsername);
      
      // Set user initiated logout flag
      localStorage.setItem('userInitiatedLogout', 'true');
    }
    
    // Reset global flag to allow reinitializing auth
    if (window && 'vfIsAuthInitialized' in window) {
      window.vfIsAuthInitialized = false;
    }
    
    // Clear authentication data
    this.clearAuth();

    // Dispatch logout event so React components can respond accordingly
    this.dispatchLogoutEvent();
  }

  getUsername(): string | null {
    return localStorage.getItem(this.USERNAME_KEY);
  }
  
  getLastUsername(): string | null {
    return localStorage.getItem(this.LAST_USERNAME_KEY);
  }

  async refreshToken(): Promise<any> {
    try {
      // Lấy token trực tiếp từ localStorage thay vì GlobalApi
      const storedToken = localStorage.getItem(this.USER_TOKEN_KEY);
      if (!storedToken) {
        return false;
      }
      
      const tokenData = JSON.parse(storedToken);
      if (!tokenData.refresh_token) {
        return false;
      }

      const data = await fetchApiLogin(
        'post',
        'token',
        { 
          grant_type: 'refresh_token',
          refresh_token: tokenData.refresh_token 
        }
      );

      if (data && data.access_token) {
        // Lưu token mới vào localStorage
        localStorage.setItem(this.USER_TOKEN_KEY, JSON.stringify(data));
        return data;
      }

      return false;
    } catch (error) {
      console.error('Refresh token failed:', error);
      return false;
    }
  }

  async storeHomeLinks(): Promise<any> {
    // Kiểm tra nếu đã có dữ liệu links trong localStorage
    const cachedLinks = localStorage.getItem(this.HOME_LINKS_KEY);
    if (cachedLinks) {
      console.log('Using cached home links');
      return JSON.parse(cachedLinks);
    }
    
    console.log('Fetching home links from API');
    try {
      const links = await fetchApi('post', 'api/Home/Links');
      if (links) {
        localStorage.setItem(this.HOME_LINKS_KEY, JSON.stringify(links));
      }
      return links;
    } catch (error: any) {
      // Chỉ clear auth nếu lỗi liên quan đến authentication
      if (error.status === 401 || error.status === 403) {
        showAlert('error', 'Vui lòng đăng nhập lại');
        this.clearAuth();
      }
      throw error;
    }
  }

  getHomeLinks() {
    const links = localStorage.getItem(this.HOME_LINKS_KEY);
    return links ? JSON.parse(links) : null;
  }

  async storeHomeLinksData(links: any): Promise<void> {
    localStorage.setItem(this.HOME_LINKS_KEY, JSON.stringify(links));
  }

  async storeProfileData(profileData: any): Promise<void> {
    console.log('AuthService: Storing profile data:', profileData);
    // Ensure we're using a consistent key and properly stringifying the data
    const profileDataString = JSON.stringify(profileData);
    
    // Store with both the class property and directly with the enum value to debug
    localStorage.setItem(this.PROFILE_DATA_KEY, profileDataString);
    
    // Double-check that the data was stored correctly
    const storedData = localStorage.getItem(this.PROFILE_DATA_KEY);
    console.log('AuthService: After storing, direct check:', storedData);
    
    if (!storedData) {
      console.error('Failed to store profile data in session storage');
    }
  }

  getProfileData(): any {
    // Try both the class property and the direct enum value
    let profileData = localStorage.getItem(this.PROFILE_DATA_KEY);
    
    console.log('AuthService: getProfileData() raw value from this.PROFILE_DATA_KEY:', profileData);
    
    if (!profileData) {
      // Try direct enum access as a fallback
      profileData = localStorage.getItem(LocalKeys.PROFILE_DATA);
      console.log('AuthService: getProfileData() raw value from direct LocalKeys.PROFILE_DATA:', profileData);
    }
    
    const parsed = profileData ? JSON.parse(profileData) : null;
    console.log('AuthService: getProfileData() parsed:', parsed);
    return parsed;
  }

  public async subscribeToNotifications() {
    try {
      // Kiểm tra trực tiếp từ localStorage thay vì GlobalApi
      const storedAuth = localStorage.getItem(this.USER_TOKEN_KEY);
      if (storedAuth) {
        const memberId = this.getUsername();

        // Make sure device ID is generated and available
        const deviceId = this.generateDeviceId();
        const fcmToken = localStorage.getItem('fcm_token');
        
        if (memberId && deviceId && fcmToken) {
          await fetchApi('post', 'api/Notification/Subscribe', {
            memberId,
            deviceId: deviceId,
            Platform: 'app',
            pushToken: fcmToken,
            title: '',
            body: ''
          }, 'json', { skipCache: true });
          console.log('Successfully subscribed to notifications');
        } else {
          console.warn('Missing required data for notification subscription:', {
            memberId,
            deviceId,
            fcmToken: localStorage.getItem('fcm_token')
          });
        }
      }
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
    }
  }

  async loadInitialData(): Promise<boolean> {
    console.log('AuthService: loadInitialData - starting...');
    
    // Ensure device ID is available
    this.generateDeviceId();
    
    // All data is now fetched directly from API when needed, we only check for profile data
    const requiredKeys = [
      LocalKeys.PROFILE_DATA
    ];
    
    const allDataExists = requiredKeys.every(key => hasLocalData(key));
    
    if (hasLocalData(LocalKeys.PROFILE_DATA)) {
      console.log('AuthService: loadInitialData - profile data in session:', 
        JSON.stringify(getLocalData(LocalKeys.PROFILE_DATA)).substring(0, 100) + '...');
    }
    
    // Nếu đã có đầy đủ dữ liệu cache, trả về true ngay
    if (allDataExists) {
      console.log('Using cached initial data - skipping API calls');
      
      // Verify SmartOTP device status even when using cached data
      this.verifySmartOTPDevice();
      
      return true;
    }
    
    console.log('Fetching initial data from API');
    try {
      // Định nghĩa các endpoint và kiểm tra cache - only for profile data and telegram id
      const apiEndpoints = [
        { key: LocalKeys.PROFILE_DATA, method: 'get', url: 'api/Account/GetProfile', defaultValue: {} },
        { key: LocalKeys.TELEGRAM_ID, method: 'get', url: 'api/Account/GetTelegramId', defaultValue: '' },
        // Other data is now fetched directly from API when needed
      ];
      
      // Tạo mảng promise dựa trên các endpoint và trạng thái cache
      const promises = apiEndpoints.map(endpoint => {
        if (hasLocalData(endpoint.key)) {
          return Promise.resolve(getLocalData(endpoint.key, endpoint.defaultValue));
        }
        
        // Thêm try-catch để xử lý lỗi và trả về giá trị mặc định thay vì phá vỡ toàn bộ promise
        try {
          return endpoint.method === 'post' 
            ? fetchApi('post', endpoint.url, {}, 'json', { skipCache: true }) 
            : fetchApi('get', endpoint.url, {}, 'json', { skipCache: true });
        } catch (error) {
          console.error(`Error fetching ${endpoint.key}:`, error);
          return Promise.resolve(endpoint.defaultValue);
        }
      });
      
      // Chạy tất cả các promise đồng thỗi
      const results = await Promise.allSettled(promises);
      
      // Xử lý kết quả từ từng promise
      const [profile, telegramId] = results.map((result, index) => {
        const endpoint = apiEndpoints[index];
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.error(`Failed to fetch ${endpoint.key}:`, result.reason);
          return endpoint.defaultValue;
        }
      });

      // Lưu trữ các responses vào session storage sử dụng utility functions
      // Cấu trúc dữ liệu cần lưu trong session
      const dataToStore = [
        { 
          key: LocalKeys.PROFILE_DATA, 
          value: profile, 
          shouldStore: profile && !hasLocalData(LocalKeys.PROFILE_DATA),
          process: (data: any) => {
            console.log('AuthService: loadInitialData storing profile data');
            return data;
          }
        },
        { 
          key: LocalKeys.TELEGRAM_ID, 
          value: telegramId, 
          shouldStore: telegramId && !hasLocalData(LocalKeys.TELEGRAM_ID) && telegramId.Status === 'Success',
          process: (data: any) => data.Message 
        }
      ];
      
      // Lưu dữ liệu vào session
      dataToStore.forEach(item => {
        try {
          if (item.shouldStore) {
            const processedValue = item.process(item.value);
            // In thông tin debug trước khi lưu vào session
            console.log(`Storing ${item.key} in session:`, 
              typeof processedValue === 'object' ? JSON.stringify(processedValue).substring(0, 100) + '...' : processedValue);
            
            setLocalData(item.key, processedValue);
            
            // Kiểm tra xác minh sau khi lưu
            const dataExists = hasLocalData(item.key);
            console.log(`Verification: ${item.key} exists in session: ${dataExists}`);
            
            if (!dataExists) {
              console.error(`Failed to store ${item.key} in loadInitialData`);
            }
          }
        } catch (error) {
          console.error(`Error when processing ${item.key}:`, error);
        }
      });

      // DISPLAY_BORROW_STOCK is now fetched directly from API when needed

      // Verify SmartOTP device status after profile is loaded
      await this.verifySmartOTPDevice();

      return true;
    } catch (error: any) {
      console.error('Load initial data failed:', error);
      
      // Nếu đã có một số dữ liệu cache, vẫn có thể sử dụng
      const hasAnyData = requiredKeys.some(key => hasLocalData(key));
      if (hasAnyData) {
        console.warn('Using partial cached data due to API error');
        return true;
      }
      
      // Kiểm tra nếu lỗi liên quan đến authentication
      if (error.status === 401 || error.status === 403) {
        // Tránh vòng lặp vô hạn bằng cách kiểm tra trạng thái refresh token
        const isRefreshing = localStorage.getItem('_REFRESHING_TOKEN_ATTEMPT');
        if (!isRefreshing) {
          try {
            // Đánh dấu đang refresh để tránh vòng lặp
            localStorage.setItem('_REFRESHING_TOKEN_ATTEMPT', 'true');
            console.log('Attempting to refresh token...');
            
            // Thử refresh token trước khi logout
            const refreshResult = await this.refreshToken();
            
            // Xóa flag refresh token
            localStorage.removeItem('_REFRESHING_TOKEN_ATTEMPT');
            
            if (refreshResult) {
              console.log('Token refreshed successfully, retrying loadInitialData');
              // Chỉ thử load lại dữ liệu một lần nữa và tránh đệ quy
              return await new Promise(resolve => {
                setTimeout(() => {
                  // Gọi qua một phiên bản hạn chế của loadInitialData
                  this.quickLoadData().then(result => resolve(result));
                }, 500);
              });
            } else {
              console.error('Token refresh failed');
              return false;
            }
          } catch (refreshError) {
            console.error('Error during token refresh:', refreshError);
            localStorage.removeItem('_REFRESHING_TOKEN_ATTEMPT');
            return false;
          }
        } else {
          console.warn('Avoiding token refresh loop - already attempting to refresh');
          return false;
        }
      }
      
      return false; // Đổi từ throw error thành return false để không làm crash app
    }
  }

  // Hàm tải dữ liệu nhanh mà không gây vòng lặp refresh token
  async quickLoadData(): Promise<boolean> {
    try {
      // Chỉ tải các dữ liệu chính yếu - bank accounts are now fetched directly when needed
      const results = await Promise.allSettled([
        fetchApi('get', 'api/Account/GetProfile'),
        fetchApi('get', 'api/Account/GetTelegramId')
      ]);
      
      // Lưu dữ liệu vào session
      const [profileResult, telegramIdResult] = results;
      
      if (profileResult.status === 'fulfilled' && profileResult.value) {
        setLocalData(LocalKeys.PROFILE_DATA, profileResult.value);
      }
      
      if (telegramIdResult.status === 'fulfilled' && telegramIdResult.value?.Status === 'Success') {
        setLocalData(LocalKeys.TELEGRAM_ID, telegramIdResult.value.Message);
      }
      
      return true;
    } catch (error) {
      console.error('quickLoadData failed:', error);
      return false;
    }
  }

  async checkAndLoadInitialData(): Promise<void> {
    // Since we now fetch data directly from API when needed, we only need to check
    // if we have a valid token and load basic profile data
    const storedTokenStr = localStorage.getItem(this.USER_TOKEN_KEY);
    let hasValidToken = false;
    
    if (storedTokenStr) {
      try {
        const tokenData = JSON.parse(storedTokenStr);
        hasValidToken = !!tokenData?.access_token;
      } catch (error) {
        console.error('Error parsing token in checkAndLoadInitialData:', error);
      }
    }
    
    // Load initial profile data if we have a valid token
    if (hasValidToken) {
      await this.loadInitialData();
    }
  }

  /**
   * Get statement overview data directly from API
   * @returns Statement overview data or null
   */
  async getStatementOverview() {
    return this.directGetStatementOverview();
  }

  /**
   * Get member stock statement data directly from API
   * @returns Member stock statement data or null
   */
  async getMemberStockStatement() {
    return this.directGetMemberStockStatement();
  }

  /**
   * Get member stock statement risky data directly from API
   * @returns Member stock statement risky data or null
   */
  async getMemberStockStatementRisky() {
    return this.directGetMemberStockStatementRisky();
  }

  /**
   * Fetches display borrow stock data directly from API
   * @returns The display borrow stock data or null
   */
  async getDisplayBorrowStock(): Promise<any> {
    return this.directGetDisplayBorrowStock();
  }
  
  /**
   * Direct API call to get display borrow stock data without using localStorage
   * @returns The display borrow stock data or null
   */
  async directGetDisplayBorrowStock(): Promise<any> {
    try {
      console.log('AuthService: directGetDisplayBorrowStock - fetching from API');
      
      const response = await fetchApi('get', 'api/BorrowStock/GetDisplayBorrowStock');
      
      console.log('AuthService: directGetDisplayBorrowStock - API response:', response);
      
      return response;
    } catch (error) {
      console.error('AuthService: directGetDisplayBorrowStock - error:', error);
      return null;
    }
  }

  /**
   * Direct API call to get display return stock data without using localStorage
   * @returns The display return stock data or null
   */
  async directGetDisplaySwapStock(): Promise<any> {
    try {
      console.log('AuthService: directGetDisplaySwapStock - fetching from API');
      
      // Thêm các tùy chọn cache rõ ràng và log chi tiết hơn
      const response = await fetchApi('get', 'api/StockSwap/GetDisplayStockSwap');
      
      console.log('AuthService: directGetDisplaySwapStock - API response:', response);
      
      // Kiểm tra kết quả đã được cache chưa
      setTimeout(() => {
        const cacheKey = `API_CACHE:GET:api/StockSwap/GetDisplayStockSwap:{}`;
        const cachedValue = sessionStorage.getItem(cacheKey);
        console.log('AuthService: directGetDisplaySwapStock - Cache check:', {
          hasCachedValue: !!cachedValue,
          cacheKey
        });
      }, 100);
      
      return response;
    } catch (error) {
      console.error('AuthService: directGetDisplaySwapStock - error:', error);
      return null;
    }
  }

  /**
   * Fetches statement overview data directly from API
   * @returns The statement overview data or null
   */
  async directGetStatementOverview(): Promise<any> {
    try {
      const response = await fetchApi('get', 'api/Home/GetStatementOverview');
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetStatementOverview - error:', error);
      return null;
    }
  }

  /**
   * Fetches member stock statement data directly from API
   * @returns The member stock statement data or null
   */
  async directGetMemberStockStatement(): Promise<any> {
    try {
      const response = await fetchApi('get', 'api/StockStatement/GetMemberStockStatement');
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetMemberStockStatement - error:', error);
      return null;
    }
  }

  /**
   * Fetches member stock statement risky data directly from API
   * @returns The member stock statement risky data or null
   */
  async directGetMemberStockStatementRisky(): Promise<any> {
    try {
      const response = await fetchApi('get', 'api/StockStatement/GetMemberStockStatementRisky');
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetMemberStockStatementRisky - error:', error);
      return null;
    }
  }

  /**
   * Fetches bank accounts data directly from API
   * @returns The bank accounts data or null
   */
  async directGetBankAccounts(): Promise<any> {
    try {
      let response = await fetchApi('get', 'api/MoneyDeposit/GetBankAccList');
      if (response) {
        if (Array.isArray(response)) {
          response = response.filter(acc => acc.Status === 'active')
        }
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetBankAccounts - error:', error);
      return null;
    }
  }

  /**
   * Fetches bank list data directly from API
   * @returns The bank list data or null
   */
  async directGetBankList(): Promise<any> {
    try {
      const response = await fetchApi('get', 'api/MoneyDeposit/GetBankAccountInfoList');
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetBankList - error:', error);
      return null;
    }
  }

  /**
   * Fetches money deposit list data directly from API
   * @returns The money deposit list data or null
   */
  async directGetMoneyDepositList(): Promise<any> {
    try {
      const response = await fetchApi('get', 'api/MoneyDeposit/GetMoneyDepositList');
      if (response) {
        return response;
      }
      return null;
    } catch (error) {
      console.error('AuthService: directGetMoneyDepositList - error:', error);
      return null;
    }
  }

  // Verify if the current device is registered for SmartOTP
  async verifySmartOTPDevice(): Promise<void> {
    try {
      const deviceId = this.generateDeviceId();
      const username = this.getUsername();
      
      if (!username) {
        console.log('Cannot verify SmartOTP device: missing username');
        return;
      }
      
      console.log('Verifying SmartOTP device registration for device:', deviceId);
      const response = await fetchApi('post', `api/SmartOTP/VerifyDeviceSmartOTP?deviceId=${deviceId}`);
      
      if (response && response.Status === 'Success') {
        console.log('Device is registered for SmartOTP');
      } else {
        console.log('Device is not registered for SmartOTP, removing OTP secrets');
        
        // Remove OTP secrets for this username
        // this.removeOtpSecrets(username);
      }
    } catch (error) {
      console.error('Failed to verify SmartOTP device:', error);
    }
  }
  
  // Remove OTP secrets for a specific username
  removeOtpSecrets(username: string): void {
    try {
      const secretsJson = localStorage.getItem('OTP_SECRETS');
      if (!secretsJson) return;
      
      const secrets = JSON.parse(secretsJson);
      if (secrets[username]) {
        delete secrets[username];
        localStorage.setItem('OTP_SECRETS', JSON.stringify(secrets));
        console.log(`OTP secrets for ${username} removed`);
      }
    } catch (error) {
      console.error('Error removing OTP secrets:', error);
    }
  }

  getForceLogoutFlag(): string | null {
    return localStorage.getItem(this.FORCE_LOGOUT_FLAG);
  }

  removeForceLogoutFlag(): void {
    localStorage.removeItem(this.FORCE_LOGOUT_FLAG);
  }

  setForceLogoutFlag(): void {
    localStorage.setItem(this.FORCE_LOGOUT_FLAG, 'true');
    // Add alert notification for force logout
    showAlert('error', 'Vui lòng đăng nhập lại');
  }

  // Dispatch logout event so React components can respond accordingly
  private dispatchLogoutEvent() {
    // Create and dispatch a custom event
    if (typeof window !== 'undefined') {
      const event = new CustomEvent(this.LOGOUT_EVENT);
      window.dispatchEvent(event);
    }
  }

  /**
   * Switch to another account
   * @param username The username of the account to switch to
   * @returns Promise with the authentication response
   */
  async switchAccount(username: string): Promise<AuthResponse> {
    try {
      console.log('[Auth] Switching to account:', username);
      
      const data = await fetchApiLogin(
        'post',
        'token',
        { targetUsername: username, grant_type: 'switch' }
      );
      
      if (data.error) {
        throw Error(data.error_description);
      }
      
      if (data) {
        // Store auth response and username directly in localStorage
        localStorage.setItem(this.USER_TOKEN_KEY, JSON.stringify(data));
        localStorage.setItem(this.USERNAME_KEY, username);
        
        // Load initial data after successful account switch
        try {
          localStorage.removeItem(this.FORCE_LOGOUT_FLAG);
          localStorage.removeItem(this.HOME_LINKS_KEY);
          localStorage.removeItem(this.PROFILE_DATA_KEY);
          localStorage.removeItem(this.TELEGRAM_ID_KEY);
          await this.loadInitialData();
        } catch (dataError) {
          console.error('Failed to load initial data after account switch:', dataError);
          // Continue with account switch even if data loading fails
        }
      }
      
      return data;
    } catch (error: any) {
      showAlert('error', error.message || 'Chuyển tài khoản không thành công');
      throw error;
    }
  }
}

export const authService = new AuthService();
